import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import React from 'react';
import { useView3DAnnotations, useView3DCameraRef } from '../../../context/context3D';

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateColumns: '200px auto 200px',
    gridTemplateRows: 'min-content auto min-content',
  },
  gridTopLeft: {
    ...shorthands.gridArea(1, 1, 2, 2),
  },
  annotationRoot: {
    marginBlock: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', '#8f8f8faf'),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  canvas: {
    width: '100%',
    height: '100%',
    ...shorthands.gridArea(1, 1, 4, 4),
  },
});

export default function App3DAnnotationOverlay(props: { className: string }) {
  const styles = useStyles();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const canvasRenderFrame = React.useRef<number>();
  const canvasCamera = useView3DCameraRef();

  const annotations = useView3DAnnotations();
  const components = annotations.map((value, i) => (
    <div
      className={mergeClasses(styles.annotationRoot)}
      style={{ backgroundColor: '#' + value.color.toString(16).padStart(6, '0') + 'ef' }}
      key={i}
      ref={value.annotationRef}
    >
      <span>{value.text}</span>
    </div>
  ));

  function updateCanvas() {
    if (canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      if (context) {
        context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        const canvasWidth = (canvasRef.current.width = canvasRef.current.offsetWidth);
        const canvasHeight = (canvasRef.current.height = canvasRef.current.offsetHeight);

        for (const annotation of annotations) {
          const container = annotation.annotationRef.current;
          if (container) {
            for (const object of annotation.ref3DObject) {
              if (!object || !canvasCamera.current) {
                continue;
              }
              const position = object.center;
              const xy = canvasCamera.current.get2DCoordinates(position);
              context.beginPath();
              context.moveTo(
                container.offsetLeft + container.offsetWidth,
                container.offsetTop + container.offsetHeight / 2,
              );
              context.lineTo(((xy.x + 1) / 2) * canvasWidth, (1 - (xy.y + 1) / 2) * canvasHeight);
              context.stroke();
            }
          }
        }
      }
    }
    canvasRenderFrame.current = requestAnimationFrame(updateCanvas);
  }

  React.useEffect(() => {
    canvasRenderFrame.current = requestAnimationFrame(updateCanvas);
    return () => {
      if (canvasRenderFrame.current) cancelAnimationFrame(canvasRenderFrame.current);
    };
  }, [updateCanvas]);

  return (
    <div className={mergeClasses(props.className, styles.root)}>
      <canvas className={styles.canvas} ref={canvasRef}></canvas>
      <div className={styles.gridTopLeft}>{components}</div>
    </div>
  );
}
